import { paginateNdtGlobalItems, type NdtGlobalListQuery, type NdtGlobalListResult } from "../../domain/ndt/ndt-global-list-query.js";
import type { RequestContext } from "../context/request-context.js";
import type { NdtCatalogRepository } from "../../repositories/operational-repositories.js";
import type { NdtMethod } from "../../domain/ndt/ndt-procedure.js";
import {
  DEFAULT_COUPLANTS,
  type NdtCatalogItem,
  type NdtCatalogItemStatus,
  type NdtCatalogKey,
  selectableCatalogItems,
  validateNdtCatalogItem
} from "../../domain/ndt/ndt-technical-catalog.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Answers whether a catalog item is referenced by any report, so a linked record is never physically deleted. */
export type NdtCatalogUsageChecker = (itemId: string) => Promise<boolean>;

export class NdtCatalogService {
  constructor(
    private readonly repo: NdtCatalogRepository,
    private readonly isItemInUse?: NdtCatalogUsageChecker
  ) {}

  /**
   * Registers the approved starting couplants (GRAXA, METIL, ÁGUA) once.
   * Idempotent and global — they must be available regardless of the client
   * selected, and an item an operator later renamed or inactivated is never
   * recreated behind their back.
   */
  async ensureDefaultCouplants(context: RequestContext): Promise<void> {
    const all = await this.repo.listAll();
    const existing = new Set(
      all.filter(i => i.catalogKey === "COUPLANT").map(i => i.name.trim().toUpperCase())
    );

    for (const name of DEFAULT_COUPLANTS) {
      if (existing.has(name)) continue;
      await this.create(context, { catalogKey: "COUPLANT", name, applicableMethods: [] });
    }
  }

  /** M21: limite 100 no backend, busca global sem escopo de Cliente. */
  async listPaged(context: RequestContext, input: NdtGlobalListQuery): Promise<NdtGlobalListResult<NdtCatalogItem>> {
    new RbacAuthorizationService().require(context, PermissionCode.NdtRegistryRead);
    const repository = this.repo as unknown as {
      listPaged?: (query: NdtGlobalListQuery) => Promise<NdtGlobalListResult<NdtCatalogItem>>;
    };
    if (repository.listPaged) return repository.listPaged(input);
    return paginateNdtGlobalItems(await this.repo.listAll(), input);
  }

  async list(context: RequestContext, catalogKey?: NdtCatalogKey): Promise<NdtCatalogItem[]> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    const filtered = catalogKey ? all.filter(i => i.catalogKey === catalogKey) : all;
    return [...filtered].sort((a, b) => a.catalogKey.localeCompare(b.catalogKey) || a.name.localeCompare(b.name));
  }

  async getById(context: RequestContext, id: string): Promise<NdtCatalogItem | null> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryRead);
    return this.repo.findById(id);
  }

  /** Items a report may offer: ACTIVE and applicable to the method, plus the one already linked to the report. */
  async listSelectableForReport(
    context: RequestContext,
    catalogKey: NdtCatalogKey,
    method?: NdtMethod,
    currentlyLinkedId?: string | null
  ): Promise<NdtCatalogItem[]> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryRead);
    const all = await this.repo.listAll();
    return selectableCatalogItems(all, catalogKey, method, currentlyLinkedId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async create(context: RequestContext, data: Partial<NdtCatalogItem>): Promise<NdtCatalogItem> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryCreate);

    const errors = validateNdtCatalogItem(data);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid catalog item: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    await this.assertNameIsUnique(data.catalogKey as NdtCatalogKey, data.name!.trim(), null);

    const now = new Date().toISOString();
    const item: NdtCatalogItem = {
      id: `CAT-${randomUUID()}`,
      catalogKey: data.catalogKey as NdtCatalogKey,
      code: data.code?.trim() || "",
      name: data.name!.trim(),
      description: data.description || "",
      status: (data.status as NdtCatalogItemStatus) || "ACTIVE",
      applicableMethods: data.applicableMethods || [],
      createdAt: now,
      updatedAt: now,
      createdBy: context.userId || "system",
      updatedBy: context.userId || "system"
    };

    await this.repo.save(item);
    return item;
  }

  async update(context: RequestContext, id: string, data: Partial<NdtCatalogItem>): Promise<NdtCatalogItem> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryUpdate);

    const existing = await this.requireItem(id);
    const merged: NdtCatalogItem = { ...existing, ...data, id: existing.id, catalogKey: existing.catalogKey, createdAt: existing.createdAt, createdBy: existing.createdBy };

    const errors = validateNdtCatalogItem(merged);
    if (errors.length > 0) {
      throw new AppError(ErrorCode.Validation, `Invalid catalog item: ${errors.join(" ")}`, {
        safeMessage: errors.join(" ")
      });
    }

    await this.assertNameIsUnique(merged.catalogKey, merged.name.trim(), existing.id);

    const updated: NdtCatalogItem = {
      ...merged,
      code: merged.code.trim(),
      name: merged.name.trim(),
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };

    await this.repo.save(updated);
    return updated;
  }

  async setStatus(context: RequestContext, id: string, status: NdtCatalogItemStatus): Promise<NdtCatalogItem> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryInactivate);

    const existing = await this.requireItem(id);
    const updated: NdtCatalogItem = {
      ...existing,
      status,
      updatedAt: new Date().toISOString(),
      updatedBy: context.userId || "system"
    };
    await this.repo.save(updated);
    return updated;
  }

  async delete(context: RequestContext, id: string): Promise<void> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.NdtRegistryDelete);

    const existing = await this.requireItem(id);

    if (this.isItemInUse && await this.isItemInUse(existing.id)) {
      throw new AppError(ErrorCode.Conflict, "Catalog item is referenced by a report", {
        safeMessage: `O registro "${existing.name}" já está vinculado a um relatório e não pode ser excluído. Utilize a ação de inativar.`
      });
    }

    await this.repo.delete(existing.id);
  }

  private async requireItem(id: string): Promise<NdtCatalogItem> {
    const existing = await this.repo.findById(id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Catalog item not found", {
        safeMessage: "Registro de catálogo não encontrado."
      });
    }
    return existing;
  }

  private async assertNameIsUnique(catalogKey: NdtCatalogKey, name: string, ignoreId: string | null): Promise<void> {
    const all = await this.repo.listAll();
    const duplicate = all.find(i =>
      i.id !== ignoreId &&
      i.catalogKey === catalogKey &&
      i.name.trim().toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, "Duplicated catalog item", {
        safeMessage: `Já existe um registro com o nome "${name}" neste catálogo.`
      });
    }
  }
}
